import { createFeature, createReducer, on } from '@ngrx/store';

import { CreateDocumentDialogActions } from './create-document-dialog.actions';
import {
  CreateDocumentDialogInput,
  CreateDocumentDialogState,
  CreateDocumentDraft,
  CreateDocumentResult,
  CreateDocumentSubmission,
  createDocumentDialogInitialState,
  newDocumentDraft,
} from './create-document-dialog.state';
import { declaredValues, errorMessage, eventsOptionSeed } from './create-document-dialog.util';
import { DocumentTypesActions, isCreatableDocumentSlot } from './document-types';

const openDialog = (
  previous: CreateDocumentDialogState,
  { jobId, docType, options }: CreateDocumentDialogInput,
): CreateDocumentDialogState => ({
  ...createDocumentDialogInitialState,
  session: previous.session + 1,
  docTypeOrder: previous.docTypeOrder,
  open: true,
  jobId,
  documents: docType ? [ newDocumentDraft(docType, options ?? {}) ] : [],
  previewing: docType,
});

const busy = (state: CreateDocumentDialogState) =>
  Boolean(state.submitting) || state.documents.some((doc) => doc.resending || doc.creating);

const editable = (state: CreateDocumentDialogState) => !busy(state) && !state.finished;

const updateDocument = (
  state: CreateDocumentDialogState,
  docType: string | undefined,
  update: (doc: CreateDocumentDraft) => CreateDocumentDraft,
): CreateDocumentDialogState => ({
  ...state,
  documents: state.documents.map((doc) => (doc.docType === docType ? update(doc) : doc)),
  unticked: state.unticked.map((doc) => (doc.docType === docType ? update(doc) : doc)),
});

const addDocument = (state: CreateDocumentDialogState, docType: string): CreateDocumentDialogState => {
  const previewed = state.documents.find((doc) => doc.docType === state.previewing);
  const draft = state.unticked.find((doc) => doc.docType === docType)
    ?? newDocumentDraft(docType, eventsOptionSeed(previewed?.values));
  return {
    ...state,
    documents: [ ...state.documents, draft ],
    unticked: state.unticked.filter((doc) => doc !== draft),
    previewing: docType,
    submitErrors: [],
  };
};

const neighbourOf = (state: CreateDocumentDialogState, docType: string, documents: CreateDocumentDraft[]) => {
  const rank = (type: string) => {
    const index = state.docTypeOrder.indexOf(type);
    return index === -1 ? state.docTypeOrder.length : index;
  };
  const ranked = [ ...documents ].sort((a, b) => rank(a.docType) - rank(b.docType));
  const removedRank = rank(docType);
  return (ranked.find((doc) => rank(doc.docType) >= removedRank) ?? ranked[ranked.length - 1])?.docType;
};

const removeDocument = (state: CreateDocumentDialogState, docType: string): CreateDocumentDialogState => {
  const removed = state.documents.find((doc) => doc.docType === docType);
  const documents = state.documents.filter((doc) => doc !== removed);
  return {
    ...state,
    documents,
    unticked: removed ? [ ...state.unticked, removed ] : state.unticked,
    previewing: state.previewing === docType ? neighbourOf(state, docType, documents) : state.previewing,
    submitErrors: [],
  };
};

const isTicked = (state: CreateDocumentDialogState, docType: string) =>
  state.documents.some((doc) => doc.docType === docType);

const previewDocument = (state: CreateDocumentDialogState, docType: string): CreateDocumentDialogState => {
  if (!editable(state)) return state;
  if (!isTicked(state, docType)) return addDocument(state, docType);
  return { ...state, previewing: docType };
};

const startSubmission = (state: CreateDocumentDialogState, submission: CreateDocumentSubmission): CreateDocumentDialogState => {
  if (!editable(state) || !state.documents.length || state.documents.some((doc) => !doc.preview)) return state;
  return { ...state, submitting: submission, submitted: submission, submitErrors: [] };
};

const recordCreation = (state: CreateDocumentDialogState, docType: string, result: CreateDocumentResult): CreateDocumentDialogState => {
  const creating = state.documents.some((doc) => doc.docType === docType && doc.creating);
  if (!state.submitting && !creating) return state;
  return finishSubmission(updateDocument(state, docType, (doc) => ({ ...doc, creating: false, result })));
};

const finishSubmission = (state: CreateDocumentDialogState): CreateDocumentDialogState => {
  if (state.documents.some((doc) => !doc.result)) return state;
  if (state.documents.some((doc) => doc.result?.artifact)) {
    return { ...state, submitting: undefined, finished: true };
  }
  return {
    ...state,
    submitting: undefined,
    submitErrors: state.documents.map((doc) => ({ docType: doc.docType, message: doc.result?.createError ?? '' })),
    documents: state.documents.map((doc) => ({ ...doc, result: undefined })),
  };
};

export const createDocumentDialogFeature = createFeature({
  name: 'createDocumentDialog',
  reducer: createReducer(
    createDocumentDialogInitialState,

    on(
      CreateDocumentDialogActions.dialogOpened,
      CreateDocumentDialogActions.jobActionsCreateDocumentClicked,
      (state, { jobId, docType, options }: CreateDocumentDialogInput): CreateDocumentDialogState =>
        openDialog(state, { jobId, docType, options }),
    ),

    on(
      CreateDocumentDialogActions.sendReceiptButtonClicked,
      (state, { jobId, transactionIds }): CreateDocumentDialogState => openDialog(state, {
        jobId,
        docType: 'receipt',
        options: transactionIds?.length ? { transactionIds } : undefined,
      }),
    ),

    on(DocumentTypesActions.loadSuccess, (state, { slots }): CreateDocumentDialogState => ({
      ...state,
      docTypeOrder: slots.filter(isCreatableDocumentSlot).map((slot) => slot.docType),
    })),

    on(
      CreateDocumentDialogActions.dialogClosed,
      CreateDocumentDialogActions.createInvoiceLinkClicked,
      (state): CreateDocumentDialogState => (busy(state) ? state : { ...state, open: false }),
    ),

    on(
      CreateDocumentDialogActions.documentCheckboxClicked,
      CreateDocumentDialogActions.documentRowClicked,
      (state, { docType }): CreateDocumentDialogState => {
        if (!editable(state)) return state;
        if (!isTicked(state, docType)) return addDocument(state, docType);
        return removeDocument(state, docType);
      },
    ),

    on(
      CreateDocumentDialogActions.previewButtonClicked,
      CreateDocumentDialogActions.needsAttentionLinkClicked,
      CreateDocumentDialogActions.previousDocumentButtonClicked,
      CreateDocumentDialogActions.nextDocumentButtonClicked,
      (state, { docType }): CreateDocumentDialogState => previewDocument(state, docType),
    ),

    on(CreateDocumentDialogActions.retryPreviewButtonClicked, (state, { docType }): CreateDocumentDialogState =>
      updateDocument(state, docType, (doc) => ({ ...doc, previewLoading: true, previewError: undefined }))),

    on(CreateDocumentDialogActions.previewLoaded, (state, { session, preview }): CreateDocumentDialogState => {
      if (session !== state.session) return state;
      return updateDocument(state, preview.docType, (doc) => {
        if (!doc.previewLoading) return doc;
        const seeded = declaredValues(preview.options, doc.seed);
        return {
          ...doc,
          preview,
          previewLoading: false,
          values: { ...preview.values, ...seeded },
          edited: Object.keys(seeded).length > 0,
        };
      });
    }),

    on(CreateDocumentDialogActions.previewFailed, (state, { session, docType, error }): CreateDocumentDialogState => {
      if (session !== state.session) return state;
      return updateDocument(state, docType, (doc) => ({
        ...doc,
        previewLoading: false,
        previewError: errorMessage(error),
      }));
    }),

    on(CreateDocumentDialogActions.optionChanged, (state, { name, value }): CreateDocumentDialogState => {
      if (!editable(state)) return state;
      return {
        ...updateDocument(state, state.previewing, (doc) => {
          const values = { ...doc.values };
          if (value === undefined) {
            delete values[name];
          } else {
            values[name] = value;
          }
          return { ...doc, values, edited: true };
        }),
        submitErrors: [],
      };
    }),

    on(CreateDocumentDialogActions.messageChanged, (state, { message }): CreateDocumentDialogState =>
      updateDocument(state, state.previewing, (doc) => ({ ...doc, message }))),

    on(CreateDocumentDialogActions.submitButtonClicked, (state, { submission }): CreateDocumentDialogState =>
      startSubmission(state, submission)),

    on(CreateDocumentDialogActions.createWithoutSendingMenuItemClicked, (state): CreateDocumentDialogState =>
      startSubmission(state, 'create')),

    on(CreateDocumentDialogActions.documentCreated, (state, { session, docType, artifact, sent, sendError }): CreateDocumentDialogState => {
      if (session !== state.session) return state;
      return recordCreation(state, docType, { artifact, sent, sendError });
    }),

    on(CreateDocumentDialogActions.createDocumentFailed, (state, { session, docType, error }): CreateDocumentDialogState => {
      if (session !== state.session) return state;
      return recordCreation(state, docType, { sent: false, createError: errorMessage(error) });
    }),

    on(CreateDocumentDialogActions.tryCreatingAgainButtonClicked, (state, { docType }): CreateDocumentDialogState => {
      if (!state.finished) return state;
      return updateDocument(state, docType, (doc) => (
        doc.creating || !doc.result?.createError ? doc : { ...doc, creating: true }
      ));
    }),

    on(CreateDocumentDialogActions.trySendingAgainButtonClicked, (state, { docType }): CreateDocumentDialogState => {
      if (!state.finished) return state;
      return updateDocument(state, docType, (doc) => (
        doc.resending || !doc.result?.artifact || doc.result.sent ? doc : { ...doc, resending: true }
      ));
    }),

    on(CreateDocumentDialogActions.resendSucceeded, (state, { session, docType }): CreateDocumentDialogState => {
      if (session !== state.session) return state;
      return updateDocument(state, docType, (doc) => (doc.result
        ? { ...doc, resending: false, result: { ...doc.result, sent: true, sendError: undefined } }
        : doc));
    }),

    on(CreateDocumentDialogActions.resendFailed, (state, { session, docType, error }): CreateDocumentDialogState => {
      if (session !== state.session) return state;
      return updateDocument(state, docType, (doc) => (doc.result
        ? { ...doc, resending: false, result: { ...doc.result, sendError: errorMessage(error) } }
        : doc));
    }),
  ),
});
